/**
 * Hover preview thumbnail for work-order intake rows (universal drag & drop
 * item 4): hovering a scanned-WO row shows the first page so files are
 * identifiable without opening the review screen.
 *
 * The intake file endpoint is auth-only (Bearer), so the preview fetches the
 * bytes as a blob and renders page 1 with pdf.js (same legacy+worker pattern
 * as PdfCanvasViewer). Rendered thumbnails cache module-wide per item id —
 * hovering the same row twice never refetches.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { fetchAuthedBlob } from "@/lib/api";
import { Loader2 } from "lucide-react";

const THUMB_WIDTH = 320;

// itemId → dataURL (or "error"). Module-level so it survives remounts.
const thumbCache = new Map<string, string>();
const inflight = new Map<string, Promise<string>>();

async function renderFirstPage(itemId: string): Promise<string> {
    const cached = thumbCache.get(itemId);
    if (cached) return cached;
    const pending = inflight.get(itemId);
    if (pending) return pending;

    const task = (async () => {
        const blob = await fetchAuthedBlob(`/api/work-order-intake/items/${itemId}/file`);
        let dataUrl: string;
        if (blob.type.startsWith("image/")) {
            dataUrl = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result));
                reader.onerror = () => reject(new Error("read failed"));
                reader.readAsDataURL(blob);
            });
        } else {
            const [pdfjsMod, workerMod] = await Promise.all([
                import("pdfjs-dist/legacy/build/pdf.mjs"),
                import("pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker"),
            ]);
            const pdfjs = pdfjsMod;
            if (!pdfjs.GlobalWorkerOptions.workerPort) {
                const PdfWorker = workerMod.default;
                pdfjs.GlobalWorkerOptions.workerPort = new PdfWorker();
            }
            const bytes = new Uint8Array(await blob.arrayBuffer());
            // pdfjs 6 removed PDFDocumentProxy.destroy(); the loading task
            // owns the document and is what frees it (and its worker transport).
            const loadingTask = pdfjs.getDocument({ data: bytes });
            const doc = await loadingTask.promise;
            const page = await doc.getPage(1);
            const base = page.getViewport({ scale: 1 });
            const scale = THUMB_WIDTH / base.width;
            const viewport = page.getViewport({ scale });
            const canvas = document.createElement("canvas");
            canvas.width = Math.ceil(viewport.width);
            canvas.height = Math.ceil(viewport.height);
            const ctx = canvas.getContext("2d");
            if (!ctx) throw new Error("no canvas context");
            await page.render({ canvasContext: ctx, viewport, canvas }).promise;
            dataUrl = canvas.toDataURL("image/jpeg", 0.8);
            void loadingTask.destroy();
        }
        thumbCache.set(itemId, dataUrl);
        return dataUrl;
    })();

    inflight.set(itemId, task);
    try {
        return await task;
    } finally {
        inflight.delete(itemId);
    }
}

/**
 * Wrap any row content; hovering (or focusing) it for a beat pops a
 * first-page thumbnail above the row.
 */
export function WoPdfHoverPreview({ itemId, children }: { itemId: string; children: ReactNode }) {
    const [visible, setVisible] = useState(false);
    const [thumb, setThumb] = useState<string | null>(thumbCache.get(itemId) ?? null);
    const [failed, setFailed] = useState(false);
    const hoverTimer = useRef<number | null>(null);

    useEffect(() => {
        if (!visible || thumb || failed) return;
        let cancelled = false;
        renderFirstPage(itemId)
            .then((url) => {
                if (!cancelled) setThumb(url);
            })
            .catch(() => {
                if (!cancelled) setFailed(true);
            });
        return () => {
            cancelled = true;
        };
    }, [visible, thumb, failed, itemId]);

    const show = () => {
        if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
        // Small delay so scrolling past rows doesn't spam fetches.
        hoverTimer.current = window.setTimeout(() => setVisible(true), 250);
    };
    const hide = () => {
        if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
        hoverTimer.current = null;
        setVisible(false);
    };

    return (
        <span className="relative inline-block min-w-0 max-w-full" onMouseEnter={show} onMouseLeave={hide}>
            {children}
            {visible && !failed && (
                <span className="pointer-events-none absolute bottom-full left-0 z-50 mb-2 block w-64 overflow-hidden rounded-lg border border-border bg-card p-1 shadow-xl">
                    {thumb ? (
                        <img src={thumb} alt="First page preview" className="block w-full rounded" />
                    ) : (
                        <span className="flex h-24 items-center justify-center">
                            <Loader2 className="h-4 w-4 animate-spin text-foreground/50" />
                        </span>
                    )}
                </span>
            )}
        </span>
    );
}
